import { c as _c } from "react/compiler-runtime";
import { postHostedUserBankLinkingLinkSessionExchange } from '@onefootprint/fetch-hosted';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { LinkBankAccount } from '@/idv2/features/link-bank-account';
import { launchMoneyKit } from '@/idv2/features/link-bank-account/launch-moneykit';
import { useFootprint } from '@/idv2/hooks/use-footprint';
import { getLogger } from '@/idv2/utils/logger';
import { suppressNextStuckDetection } from '@/idv2/utils/route-tracking';
const {
  logInfo,
  logError
} = getLogger({
  name: 'idv2',
  location: 'link-bank-account-route'
});
export const Route = createFileRoute('/onboarding/$token/link-bank-account')({
  component: LinkBankAccountPage,
  loader: async ({
    context: {
      currentRequirement
    }
  }) => {
    if (currentRequirement?.kind !== 'link_bank_account') {
      throw new Error(`Invalid requirement kind: ${currentRequirement?.kind ?? 'undefined'}`);
    }
    logInfo('Link bank account loader started', {
      requirementKind: currentRequirement.kind,
      isExistingLink: currentRequirement.isExistingLink
    });
    return {
      currentRequirement
    };
  }
});

/*
  This component links a bank account via MoneyKit and in one of two states:
  - loading: MoneyKit is open (spinner, no CTA)
  - ready: user exited MoneyKit or an error occurred (no spinner, CTA)

  Important: MoneyKit linkSessionTokens are single-use. If the user exits without completing,
  we must navigate back to check-requirements to get a fresh token before re-launching.
*/

export function LinkBankAccountPage() {
  const $ = _c(16);
  const {
    currentRequirement
  } = Route.useLoaderData();
  const {
    token: authToken
  } = Route.useParams();
  const footprint = useFootprint();
  const storageKey = `fp-link-bank-launched-${authToken}`;
  let t0;
  if ($[0] !== storageKey) {
    t0 = typeof window !== "undefined" && !sessionStorage.getItem(storageKey);
    $[0] = storageKey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isFirstTimeRunning = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      retry: false,
      mutationFn: _temp
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const linkMutation = useMutation(t1);
  let t2;
  if ($[3] !== authToken || $[4] !== currentRequirement || $[5] !== footprint || $[6] !== linkMutation) {
    t2 = async () => {
      const {
        linkSessionToken: linkSessionToken_0,
        isExistingLink: isExistingLink_0
      } = currentRequirement;
      ;
      try {
        const result = await linkMutation.mutateAsync({
          authToken,
          linkSessionToken: linkSessionToken_0,
          isExistingLink: isExistingLink_0
        });
        if (result.linked) {
          logInfo("Link bank account completed successfully");
        } else {
          logInfo("Link bank account exited by user, refreshing session token");
          suppressNextStuckDetection();
        }
        footprint.navigation.forward();
      } catch (t3) {
        const error = t3;
        logError("Link bank account failed", error);
      }
    };
    $[3] = authToken;
    $[4] = currentRequirement;
    $[5] = footprint;
    $[6] = linkMutation;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const initialize = t2;
  let t3;
  if ($[8] !== initialize || $[9] !== isFirstTimeRunning || $[10] !== storageKey) {
    t3 = () => {
      if (isFirstTimeRunning) {
        sessionStorage.setItem(storageKey, "true");
        initialize();
      }
    };
    $[8] = initialize;
    $[9] = isFirstTimeRunning;
    $[10] = storageKey;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  if ($[13] !== initialize || $[14] !== linkMutation.isPending) {
    t5 = <LinkBankAccount isLoading={linkMutation.isPending} onLaunch={initialize} />;
    $[13] = initialize;
    $[14] = linkMutation.isPending;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}
async function _temp(t0) {
  const {
    authToken: authToken_0,
    linkSessionToken,
    isExistingLink
  } = t0;
  const {
    exchangeableToken,
    type: launchResultType
  } = await launchMoneyKit({
    linkSessionToken,
    isExistingLink
  });
  if (launchResultType === "exit") {
    logInfo("MoneyKit exited by user");
    return {
      linked: false
    } as const;
  }
  if (exchangeableToken) {
    logInfo("Exchanging MoneyKit token");
    await postHostedUserBankLinkingLinkSessionExchange({
      body: {
        exchangeableToken
      },
      headers: {
        "X-Fp-Authorization": authToken_0
      }
    });
  }
  return {
    linked: true
  } as const;
}